import { Component, EventEmitter, Output, OnDestroy } from '@angular/core'
import { ClusteringNormalization, PipelineType, TsneParameters } from '../../../shared/model/jobs'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { combineLatest, Subject, take, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import { MarkerDimension } from '../../../state/case/case.state'
import {
    selectCaseMarkers,
    selectCaseSegObjectPropertyMap,
    selectCaseTsneResults,
} from '../../../state/case/case.selectors'
import { selectJobs } from '../../../state/job/job.selectors'
import { environment } from 'src/environments/environment'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { selectImageLayers, selectAnnotationLayers } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-tsne-job',
    templateUrl: './tsne-job.component.html',
    styleUrls: ['./tsne-job.component.scss'],
})
export class TsneJobComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    tsneForm: FormGroup

    formIsVisible = false
    createJobMode = true
    readonly clusteringMarkers$ = this.store.select(selectCaseMarkers)
    readonly tsneResults$ = this.store.select(selectCaseTsneResults)
    readonly imageLayers$ = this.store.select(selectImageLayers)
    readonly annotationLayers$ = this.store.select(selectAnnotationLayers)
    readonly SELECTED_EXPERIMENT_NAME_KEY = 'selectedExperimentName'
    readonly NEW_EXPERIMENT_NAME_KEY = 'newExperimentName'
    readonly LAYER_TYPE_MARKERS_KEY = 'markers'
    readonly LAYER_TYPE_TRANSCRIPTS_KEY = 'transcripts'
    readonly LAYER_TYPE_MORPHOMETRICS_KEY = 'morphometrics'
    readonly PERPLEXITY_FORM_KEY = 'perplexity'
    readonly NUMBER_OF_ITERATIONS_FORM_KEY = 'numberOfIterations'
    readonly NORMALIZATION_FORM_KEY = 'normalization'

    experiments = [{ name: 'Create new', x: 'TSNE1_X', y: 'TSNE1_Y' }]

    layerTypes: { name: string; formControl: string; disabled: boolean }[] = []

    private readonly destroy$ = new Subject<void>()
    jobIsRunning = false
    private readonly wikiUrl = environment.wikiUrl
    readonly selectedSegObjectPropertyMap$

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.buildForm(this.experiments[0])

        this.store.select(selectJobs).subscribe(jobs => {
            const job = jobs.find(
                j => j.type === 'TSN' && j.status !== 'Rejected' && j.status !== 'Closed'
            )
            this.jobIsRunning = job ? true : false
        })

        this.selectedSegObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
        this.selectedSegObjectPropertyMap$.pipe(takeUntil(this.destroy$)).subscribe(map => {
            this.tsneResults$.pipe(take(1)).subscribe(tsne => {
                this.experiments = [
                    this.experiments[0],
                    ...tsne
                        .filter(t => t.seg_object_property_map_name === map?.name)
                        .map(t => ({
                            name: t.name,
                            x: t.x,
                            y: t.y,
                            perplexity: t.perplexity,
                            numberOfIterations: t.number_of_iterations,
                            normalization: t.normalization,
                            markers: t.markers,
                            transcripts: t.transcripts,
                            morphometrics: t.morphometrics,
                        })),
                ]
            })
        })

        combineLatest([this.imageLayers$, this.annotationLayers$])
            .pipe(take(1))
            .subscribe(([imageLayers, annotationLayers]) => {
                this.layerTypes = [
                    {
                        name: 'Marker',
                        formControl: this.LAYER_TYPE_MARKERS_KEY,
                        disabled: imageLayers.length === 0,
                    },
                    {
                        name: 'Transcripts',
                        formControl: this.LAYER_TYPE_TRANSCRIPTS_KEY,
                        disabled: annotationLayers.filter(l => l.name !== 'selection').length === 0,
                    },
                    {
                        name: 'Morphometrics',
                        formControl: this.LAYER_TYPE_MORPHOMETRICS_KEY,
                        disabled: false,
                    },
                ]
            })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<TsneParameters>()

    get paramsValid(): boolean {
        if (
            !this.tsneForm.value.markers &&
            !this.tsneForm.value.transcripts &&
            !this.tsneForm.value.morphometrics
        ) {
            return false
        }
        return this.tsneForm.valid
    }

    confirmParams(): void {
        if (!this.paramsValid) return
        this.selectedSegObjectPropertyMap$.pipe(take(1)).subscribe(map => {
            if (!map) {
                this.toaster.httpError(
                    new HttpErrorResponse({ error: 'Select an intensity map' }),
                    ''
                )
                return
            }
            const inputFeature = ['markers', 'morphometrics', 'transcripts'].filter(
                feature => this.tsneForm.value[feature]
            )
            delete this.tsneForm.value.selectedExperimentName
            this.jobApi
                .startClusteringJob({
                    type: PipelineType.tsneClustering,
                    ...this.tsneForm.value,
                    input_feature: inputFeature,
                    seg_object_property_map: map,
                })
                .subscribe({
                    next: () => closeJobSelectionDialog(),
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Error starting job')
                    },
                })
            this.jobIsRunning = true
        })
    }

    displayForm(): void {
        this.formIsVisible = !this.formIsVisible
        this.createJobMode = true
        if (this.formIsVisible) {
            this.buildForm(this.experiments[0])
        }
    }

    setExperiment(experimentName: string): void {
        if (experimentName === 'Create new') {
            this.createJobMode = true
        } else {
            const experiment = this.experiments.find(ex => ex.name === experimentName)
            if (!experiment) return
            this.createJobMode = false
            this.buildForm(experiment)
            this.setDataAxis(experiment.x, experiment.y)
        }
    }

    buildForm(experiment: any) {
        this.tsneForm = this.formBuilder.group({
            [this.SELECTED_EXPERIMENT_NAME_KEY]: new FormControl(experiment?.name, [
                Validators.required,
            ]),
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl('', [Validators.required]),
            [this.LAYER_TYPE_MARKERS_KEY]: new FormControl(experiment?.markers),
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: new FormControl(experiment?.transcripts),
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: new FormControl(experiment?.morphometrics),
            [this.PERPLEXITY_FORM_KEY]: new FormControl(experiment?.perplexity, [
                Validators.required,
                Validators.min(1),
            ]),
            [this.NUMBER_OF_ITERATIONS_FORM_KEY]: new FormControl(experiment?.numberOfIterations, [
                Validators.required,
                Validators.min(250),
            ]),
            [this.NORMALIZATION_FORM_KEY]: new FormControl(experiment?.normalization, [
                Validators.required,
            ]),
        })
    }

    setDataAxis(markerXAxis: string, markerYAxis: string): void {
        this.store
            .select(selectCaseMarkers)
            .pipe(take(1))
            .subscribe((e: MarkerDimension[]) => {
                const markerX = e.find(m => m.name === markerXAxis)
                const markerY = e.find(m => m.name === markerYAxis)
                if (!markerX || !markerY) return

                this.store.dispatch(
                    plotDataActions.loadDataBothAxes({
                        markerXAxis: markerX,
                        markerYAxis: markerY,
                    })
                )
            })
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Working_with_jobs#h-7-umap-and-t-sne`, '_blank')
    }
}
